import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { EMPTY_VALUE, TONE } from '../../data/constants'
import { formatRelativeDate } from '../../data/reportCopy'
import { getCapabilityGap } from '../../data/scoring'
import { buildSharePointTenantSettingRows } from '../../data/sharePointTenantSettings'
import Chip from '../../components/Chip/Chip'
import ReportTable from '../../components/ReportTable/ReportTable'
import { useAIRiskReportChannel } from '../../components/RenderMode/AIRiskReportRenderModeProvider'
import SettingNameCell from '../../components/SettingNameCell/SettingNameCell'
import { AI_RISK_REPORT_CHANNELS } from '../../data/reportChannel'
import SubArea from '../../components/SubArea/SubArea'
import SubBlock from '../../components/SubBlock/SubBlock'
import UnavailableCell from '../../components/UnavailableCell/UnavailableCell'
import SharePointSitesTable from './SharePointSitesTable/SharePointSitesTable'

const STALE_WARN_DAYS = 30
const STALE_DANGER_DAYS = 90
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

const getStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())
const hasDisplayValue = (value) => value !== undefined && value !== null && value !== ''

const getStalenessTone = (value) => {
  if (!value) return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  const daysAgo = Math.max(0, Math.floor((getStartOfDay(new Date()) - getStartOfDay(date)) / MILLISECONDS_PER_DAY))
  if (daysAgo > STALE_DANGER_DAYS) return TONE.DANGER
  if (daysAgo > STALE_WARN_DAYS) return TONE.WARN
  return null
}

const renderStaleDate = (intl, value) => {
  const label = formatRelativeDate(intl, value)
  const tone = getStalenessTone(value)
  if (!tone) return label
  return <span className={`${tone === TONE.DANGER ? 'text-danger' : 'text-warning'} fw-bold`}>{label}</span>
}

const renderCurrent = (row) => {
  if (row.licenseRequired) return <Chip tone={TONE.INFO}>{row.current}</Chip>
  if (row.applicable === false) {
    return (
      <div>
        <UnavailableCell messageId="AI_RISK_REPORT.NOT_APPLICABLE" />
        <div className="small text-muted mt-1">
          <FormattedMessage id={row.notApplicableMessageId} />
        </div>
      </div>
    )
  }
  if (!hasDisplayValue(row.current) || row.tone === TONE.NEUTRAL) return <UnavailableCell />

  let value = row.current
  if (row.tone === TONE.DANGER) value = <strong className="text-danger fw-bold">{row.current}</strong>
  if (row.tone === TONE.WARN) value = <strong className="text-warning fw-bold">{row.current}</strong>

  return value
}

const renderRecommended = (row) => (row.applicable === false ? EMPTY_VALUE : row.recommended)

const renderSettingName = (row) => (
  <SettingNameCell
    labelId={row.labelId}
    sourceKey={row.sourceKey}
    tooltipElementId={`ai-risk-setting-tip-${row.key}`}
    tooltipMessageId={row.tooltipId}
  />
)

export default function SharePoint({
  tenantSettingAssessments = [],
  sites,
  siteTotal,
  oversharedSiteCount,
  applicationAccessSiteCount,
  score,
  gap = null,
  partialGaps,
}) {
  const intl = useIntl()
  const channel = useAIRiskReportChannel()
  const sitesTitleId =
    channel === AI_RISK_REPORT_CHANNELS.BETA
      ? 'AI_RISK_REPORT.DATA.SHAREPOINT.SITES_TITLE_BETA'
      : 'AI_RISK_REPORT.DATA.SHAREPOINT.SITES_TITLE'
  const siteGap = gap ?? getCapabilityGap(partialGaps, 'oversharedSites')
  const applicationAccessGap = gap ?? getCapabilityGap(partialGaps, 'applicationAccess')
  const settingsGap = gap ?? getCapabilityGap(partialGaps, 'tenantSettings')
  const settingsRows = buildSharePointTenantSettingRows(tenantSettingAssessments, intl).map((row) => ({
    ...row,
    tone: row.licenseRequired ? TONE.INFO : ({ true: TONE.OK, false: TONE.DANGER }[String(row.result)] ?? TONE.NEUTRAL),
  }))
  const assessedSettings = settingsRows.filter(({ result }) => result !== null)
  const recommendedSettingsCount = assessedSettings.filter(({ result }) => result).length
  const renderSiteDate = React.useCallback((value) => renderStaleDate(intl, value), [intl])

  const stats = [
    {
      key: 'recommendedSettings',
      value: settingsGap || settingsRows.length === 0 ? EMPTY_VALUE : recommendedSettingsCount,
      denom: settingsGap || assessedSettings.length === 0 ? undefined : assessedSettings.length,
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.SHAREPOINT.STAT_RECOMMENDED_SETTINGS" />,
    },
    {
      key: 'overshared',
      value: siteGap || !Number.isFinite(oversharedSiteCount) ? EMPTY_VALUE : oversharedSiteCount,
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.SHAREPOINT.STAT_OVERSHARED" />,
    },
    {
      key: 'appAccess',
      value:
        applicationAccessGap || !Number.isFinite(applicationAccessSiteCount) ? EMPTY_VALUE : applicationAccessSiteCount,
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.SHAREPOINT.STAT_APP_ACCESS" />,
    },
  ]

  const settingsColumns = [
    {
      key: 'setting',
      header: <FormattedMessage id="AI_RISK_REPORT.SETTINGS.COL_SETTING" />,
      render: renderSettingName,
      sortable: true,
      sortValue: (row) => intl.formatMessage({ id: row.labelId }),
      searchValue: (row) => [intl.formatMessage({ id: row.labelId }), row.sourceKey, row.key],
    },
    {
      key: 'current',
      header: <FormattedMessage id="AI_RISK_REPORT.SETTINGS.COL_CURRENT" />,
      render: renderCurrent,
      sortable: true,
      sortValue: (row) => row.current,
      searchValue: (row) => {
        if (row.applicable === false) {
          return [
            intl.formatMessage({ id: 'AI_RISK_REPORT.NOT_APPLICABLE' }),
            intl.formatMessage({ id: row.notApplicableMessageId }),
          ]
        }
        if (!hasDisplayValue(row.current) || row.tone === TONE.NEUTRAL) {
          return intl.formatMessage({ id: 'AI_RISK_REPORT.UNAVAILABLE' })
        }
        return row.current
      },
    },
    {
      key: 'recommended',
      header: <FormattedMessage id="AI_RISK_REPORT.SETTINGS.COL_RECOMMENDED" />,
      render: renderRecommended,
      sortable: true,
      sortValue: (row) => row.recommended,
      searchValue: (row) => (row.applicable === false ? EMPTY_VALUE : row.recommended),
    },
  ]

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.DATA.SHAREPOINT.TITLE"
      subtitleId="AI_RISK_REPORT.DATA.SHAREPOINT.SUBTITLE"
      score={score.value}
      tone={score.tone}
      gap={gap}
      partialGap={partialGaps[0]}
      stats={stats}
    >
      <SubArea titleId="AI_RISK_REPORT.DATA.SHAREPOINT.SETTINGS_TITLE">
        <ReportTable
          columns={settingsColumns}
          rows={settingsRows}
          affectedEntityLabelId="AI_RISK_REPORT.TABLE.ENTITY.SETTINGS"
          isAffectedEntityTable={false}
        />
      </SubArea>
      <SubArea titleId={sitesTitleId}>
        <SharePointSitesTable
          sites={siteGap ? [] : sites}
          total={siteGap ? 0 : siteTotal}
          renderDate={renderSiteDate}
          isAssessed={!siteGap}
        />
      </SubArea>
    </SubBlock>
  )
}

SharePoint.propTypes = {
  tenantSettingAssessments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  sites: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  siteTotal: PropTypes.number.isRequired,
  oversharedSiteCount: PropTypes.number,
  applicationAccessSiteCount: PropTypes.number,
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  gap: PropTypes.shape({ reason: PropTypes.string }),
  partialGaps: PropTypes.arrayOf(PropTypes.shape({ capabilityId: PropTypes.string })).isRequired,
}
